import { GridColumn } from "@progress/kendo-react-grid";
import { FilterDescriptor, SortDescriptor } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Window } from '@progress/kendo-react-dialogs';
import { Popup } from '@progress/kendo-react-popup';
import { Menu, MenuItem } from "@progress/kendo-react-layout";
import { protectedResources } from '../../authConfig';
import BaseGrid, { ButtonHelper } from '../../components/KendoGrid/BaseGrid';
import { AppUserContext } from "../../components/PvsContextProvider";
import { useContext, useState } from "react";
import { DropdownFilterCell } from "../../components/KendoGrid/DropDownFilterCell";
import { getReportURL } from "../../utils/reportUtil";
import { ResultType, useFetchWithMsal } from "../../hooks/useFetchWithMsal";
import { LoadPage } from "../../components/LoadPage";
import { ReportStatus, ReportStatusArray } from "../../models/reportStatus";
import { useNotificationAPI } from "../../components/NotificationProvider";
import { RadioButton, TextBox } from "@progress/kendo-react-inputs";

interface ReportListProps {
    submittedKitId?: number,
    labId?: number,
    narrowList?: boolean,
    children?: React.ReactNode
}

interface ToggleReportStatusWindowProps {
    action: string,
    report: any
}

const pageTitle = 'Report List';
const ReportList = (props: ReportListProps) => {
    const appUserContext = useContext(AppUserContext);
    const notifcations = useNotificationAPI();
    const [showAllReports, setShowAllReports] = useState(false);
    const [tableKey, setTableKey] = useState(Date.now());
    const [reportStatusWindowProp, setReportStatusWindowProp] = useState(undefined as ToggleReportStatusWindowProps | undefined);
    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const [sendReport, setSendReport] = useState(undefined as any | undefined);
    const [sendEmailToLab, setSendEmailToLab] = useState(true);
    const [sendEmailToAddress, setSendEmailToAddress] = useState("");

    const recentEntriesFirstSort = [{
        field: "Id",
        dir: "desc"
    } as SortDescriptor];

    //We don't want to show voided items by default
    const reportStatusFilter = {
        field: "reportStatusId",
        operator: "lt",
        value: ReportStatus.VoidedReview,
    };
    let gridFilter = undefined as FilterDescriptor[] | undefined;

    if (props.submittedKitId) {
        gridFilter = [{
            field: "SubmittedKitId",
            operator: "eq",
            value: props.submittedKitId,
        }] as FilterDescriptor[];
    } else if (props.labId) {
        gridFilter = [{
            field: "submittedKit.labId",
            operator: "eq",
            value: props.labId,
        }] as FilterDescriptor[];
    } else {
        gridFilter = undefined;
    }

    const FacilityAccountNumberCell = (report: any) => {
        const facilityAccountNumber = report.dataItem.submittedKit.lab.id;
        return <td>
            {String(facilityAccountNumber).padStart(5, '0')}
        </td>;
    }

    const ReportStatusCell = (report: any) => {
        return <td>{ReportStatusArray[report.dataItem.reportStatusId - 1].name}</td>;
    }

    const DateCell = (report: any) => {
        var date = new Date(report.dataItem.createdOn);
        return <td>
            {date.toLocaleDateString()}
        </td>;
    }

    const ReportStatusFilterCell = (props: any) => (
        <DropdownFilterCell
            {...props}
            data={ReportStatusArray}
            defaultItem={""}
            textField={"name"}
        />
    );

    const GenerateReport = () => {
        execute("GET", protectedResources.apiReport.endpoint + "GenerateReport?kitId=" + props.submittedKitId, undefined, undefined, ResultType.None).then((data: any) => {
            if (data === undefined) {
                notifcations.addNotification('Report generated for Submitted Kit ' + props.submittedKitId, pageTitle, 'success');
            }
        })
    }

    const updateReportStaus = () => {
        execute("POST", protectedResources.apiReport.endpoint + reportStatusWindowProp?.action + "Report", { Id: reportStatusWindowProp?.report.id }).then((data: any) => {
            if (data?.item?.id && reportStatusWindowProp?.report) {
                reportStatusWindowProp.report.reportStatusId = data.item.reportStatusId;
                notifcations.addNotification('Report for Submitted Kit ' + reportStatusWindowProp.report.submittedKit.barcode + ' had it\'s Report Status updated to ' + ReportStatusArray[reportStatusWindowProp.report.reportStatusId - 1].name, 'Report List', 'success');
            }
            setReportStatusWindowProp(undefined);
        })
    }

    const sendEmail = () => {
        if (sendEmailToLab || sendEmailToAddress) {
            execute("POST", protectedResources.apiReport.endpoint + "SendReport", { ReportId: sendReport.id, EmailAddress: sendEmailToAddress, SendToLab: sendEmailToLab }).then((data: any) => {
                if (data?.notifications && !data.notifications.hasErrors) {
                    notifcations.addNotification('Report sent for ' + sendReport?.submittedKit?.barcode + ' to ' + (sendEmailToLab ? 'the lab' : sendEmailToAddress), pageTitle, 'success');
                    setSendReport(undefined);
                }
            })
        } else {
            notifcations.addNotification('You must specify an email address if not sending the report to a lab.', pageTitle, 'info');
        }
    }

    const openReportTab = (reportId: any) => {
        const reportURLFunction = (reportUrl: string) => {
            window.open(reportUrl);
        };
        const blobIsEmptyOrUndefined = () => {
            if (appUserContext.adminView) {
                notifcations.addNotification("There was an error opening the report for this kit. Please contact IT to resolve this issue. ReportID: " + reportId, pageTitle, "info");
            } else {
                notifcations.addNotification("There was an error opening the report for this kit. Please contact STATS to resolve this issue. ReportID: " + reportId, pageTitle, "info");
            }
        };

        getReportURL(execute, protectedResources.apiReport.endpoint + "GetDocumentByReportId?Id=" + reportId, reportURLFunction, blobIsEmptyOrUndefined);
    }

    const menuSelect = (event: any) => {
        if (event.item.text === "View Report") {
            openReportTab(event.item.data.id);
        } else if (event.item.text === "Email Report") {
            setSendReport(event.item.data);
        } else if (event.item.text === "Void Report") {
            setReportStatusWindowProp({ action: "Void", report: event.item.data });
        } else if (event.item.text === "Include Report") {
            setReportStatusWindowProp({ action: "Include", report: event.item.data });
        }

        setReportOption(undefined);
    }

    const [menuAnchor, setMenuAnchor] = useState<HTMLTableCellElement | undefined>(undefined);
    const [reportOption, setReportOption] = useState(undefined as any | undefined);

    const pdfCell = (props: any) => {
        return (
            <td onClick={(e) => {
                setMenuAnchor(e.currentTarget);
                setReportOption(reportOption === props?.dataItem ? undefined : props?.dataItem);
            }} >
                <span className="k-icon k-font-icon k-i-pdf"></span>
            </td>
        )
    }

    let buttonArray = undefined as ButtonHelper[] | undefined;
    if (appUserContext.isStreckAdmin) {
        buttonArray = [{ buttonText: (showAllReports ? "Hide" : "Show") + " voided reports", onClick: () => { setShowAllReports(!showAllReports); setTableKey(Date.now()); } } as ButtonHelper];
        if (props.submittedKitId) {
            buttonArray?.push({ buttonText: "Generate Report", onClick: () => { GenerateReport(); } } as ButtonHelper);
        }
    }

    const gridProps = {
        onRowDoubleClick: openReportTab,
        filter: gridFilter,
        button: buttonArray,
        overrideableFilter: showAllReports ? undefined : reportStatusFilter,
        intialSort: recentEntriesFirstSort,
        key: tableKey,
        verb: 'POST',
        pageTitle: 'Report List',
        endpoint: protectedResources.apiReport.endpoint + "KendoGridRead",
        excelFunction: (report: any) => {
            const r = { ...report };
            if (r.reportStatusId !== undefined) {
                r.reportStatusId = ReportStatusArray[report.reportStatusId - 1].name;
            }

            return r;
        }
    }

    return (
        <>
            {reportStatusWindowProp &&
                <Window onClose={() => setReportStatusWindowProp(undefined)}>
                    <span>
                        Are you sure you want to {reportStatusWindowProp.action} this report?<br></br><br></br>
                        <Button onClick={() => updateReportStaus()}>Yes</Button>
                    </span>
                </Window>}

            <Popup
                anchor={menuAnchor}
                anchorAlign={{
                    horizontal: "right",
                    vertical: "bottom"
                }}
                popupAlign={{
                    horizontal: "left",
                    vertical: "bottom"
                }}
                show={menuAnchor !== null}>
                {reportOption !== undefined &&
                    <Menu vertical={true} onSelect={menuSelect}>
                        <MenuItem data={reportOption} text="View Report"></MenuItem>
                        <MenuItem data={reportOption} text="Email Report"></MenuItem>

                        {(reportOption?.reportStatusId === ReportStatus.VoidedFinal || reportOption?.reportStatusId === ReportStatus.VoidedReview) && appUserContext.isStreckAdmin &&
                            <MenuItem data={reportOption} text="Include Report"></MenuItem>}
                        {(reportOption?.reportStatusId === ReportStatus.Final || reportOption?.reportStatusId === ReportStatus.Review) && appUserContext.isStreckAdmin &&
                            <MenuItem data={reportOption} text="Void Report"></MenuItem>}
                    </Menu>}
            </Popup>

            {sendReport &&
                <Window onClose={() => setSendReport(undefined)}>
                    <span>
                        <RadioButton
                            name="sendReportGroup"
                            value={true}
                            checked={sendEmailToLab === true}
                            label="Send Email to Lab"
                            onChange={() => setSendEmailToLab(true)}
                        /><br></br>
                        <RadioButton
                            name="sendReportGroup"
                            value={false}
                            checked={sendEmailToLab === false}
                            label="Send Email to Email Address"
                            onChange={() => setSendEmailToLab(false)}
                        /><br></br>
                        {!sendEmailToLab &&
                            <>
                                Email Address: <br></br>
                                <TextBox value={sendEmailToAddress} onChange={(data: any) => { setSendEmailToAddress(data.value); }}></TextBox>
                                <br></br>
                            </>}<br></br>
                        <Button onClick={() => sendEmail()}>Email Report</Button>
                    </span>
                </Window>}
            <LoadPage isLoading={isLoading}>
                {props.narrowList ?
                    <BaseGrid {...gridProps}>
                        <GridColumn width="35px" cell={pdfCell} filterable={false} sortable={false} />
                        <GridColumn field="submittedKit.pipet.serialNumber" title="Pipet Name" filter="text" />
                        <GridColumn field="submittedKit.pipetDeliveryVolume" title="Volume" filter="numeric" />
                        <GridColumn field="submittedKit.lab.facilityName" title="Facility Name" filter="text" />
                        {appUserContext.adminView &&
                            <GridColumn field="reportStatusId" title="Status" filter="text" cell={ReportStatusCell} filterCell={ReportStatusFilterCell} />}
                        <GridColumn field="createdOn" title="Created On" filter="date" cell={DateCell} />
                    </BaseGrid> :

                    <BaseGrid {...gridProps}>
                        <GridColumn width="35px" cell={pdfCell} filterable={false} sortable={false} />
                        <GridColumn field="submittedKit.barcode" title="Kit Barcode" filter="text" />
                        {props.labId === undefined && appUserContext.adminView &&
                            <GridColumn field="submittedKit.lab.id" title="Facility Account Number" filter="numeric" cell={FacilityAccountNumberCell} />}
                        {props.labId === undefined &&
                            <GridColumn field="submittedKit.lab.facilityName" title="Facility Name" filter="text" />}
                        <GridColumn field="submittedKit.operator.operatorName" title="Operator Name" filter="text" />
                        <GridColumn field="submittedKit.pipet.serialNumber" title="Pipet Name" filter="text" />
                        <GridColumn field="submittedKit.pipetDeliveryVolume" title="Volume" filter="numeric" />
                        {appUserContext.adminView &&
                            <GridColumn field="reportStatusId" title="Status" filter="text" cell={ReportStatusCell} filterCell={ReportStatusFilterCell} />}
                        <GridColumn field="createdOn" title="Created On" filter="date" cell={DateCell} />
                    </BaseGrid>}
            </LoadPage>
        </>
    );
}

export default ReportList;